import { action, computed, observable } from 'mobx';
import { setter } from 'mobx-decorators';
import { deserializeTimestamp, deserializeWithMappers, setFieldValues } from 'stores/Utils';
import constants from 'Constants';


class AccountStore {

  @setter @observable id;
  @setter @observable name;
  @setter @observable disableSending;
  @setter @observable ecommercePlatform;
  @setter @observable billedBy;
  @setter @observable hasStartedTrial;
  @setter @observable createdDatetime;
  @setter @observable data;

  static create(data={}) {
    const instance = new AccountStore();
    setFieldValues(instance, data);
    return instance;
  }

  static getDefaults() {
    return {
      "id": null,
      "name": "",
      "disableSending": false,
      "ecommercePlatform": "",
      "billedBy": "",
      "hasStartedTrial": false,
      "createdDatetime": null,
      "data": {},
    };
  }

  constructor(defaults=AccountStore.getDefaults()) {
    setFieldValues(this, defaults);
  }

  @action reset(defaults=AccountStore.getDefaults()) {
    setFieldValues(this, defaults);
  }

  getDeserializeMappers() {
    return [
      {"field": "id"},
      {"field": "name"},
      {"field": "disableSending", "accessor": "disable_sending"},
      {"field": "ecommercePlatform", "accessor": "ecommerce_platform"},
      {"field": "billedBy", "accessor": "billed_by"},
      {"field": "hasStartedTrial", "accessor": "has_started_trial"},
      {"field": "createdDatetime", "accessor": "created_timestamp", "type": constants.DESERIALIZE_TYPE_CUSTOM, "extract": deserializeTimestamp},
      {"field": "data"},
    ];
  }

  @action deserialize(data) {
    deserializeWithMappers(this, this.getDeserializeMappers(), data);
  }

  @computed get isSuspended() {
    return this.organization.isSuspended;
  }

  @computed get eligibleSending() {
    return this.organization.eligibleSending;
  }

  @computed get canSend() {
    if (this.disableSending) return false;
    if (this.isSuspended) return false;
    if (!this.eligibleSending) return false;
    return true;
  }

  @computed get isEcommercePlatformShopify() {
    return this.ecommercePlatform === constants.ECOMMERCE_PLATFORM_SHOPIFY;
  }

  @computed get isEcommercePlatformBigCommerce() {
    return this.ecommercePlatform === constants.ECOMMERCE_PLATFORM_BIGCOMMERCE;
  }

  @computed get isEcommercePlatformEasyStore() {
    return this.ecommercePlatform === constants.ECOMMERCE_PLATFORM_EASYSTORE;
  }

  @computed get isEcommercePlatformShopline() {
    return this.ecommercePlatform === constants.ECOMMERCE_PLATFORM_SHOPLINE;
  }

  @computed get isEcommercePlatformWix() {
    return this.ecommercePlatform === constants.ECOMMERCE_PLATFORM_WIX;
  }

  @computed get isEcommercePlatformOthers() {
    return this.ecommercePlatform === constants.ECOMMERCE_PLATFORM_OTHERS;
  }

  @computed get ecommercePlatformDisplay() {
    const option = constants.ECOMMERCE_PLATFORM_OPTIONS.filter((option) => option.value === this.ecommercePlatform)[0];
    if (option) return option.label;
    return "";
  }

  @computed get billedByOnVoard() {
    return this.billedBy === constants.BILLED_BY_ONVOARD;
  }

  @computed get billedByShopify() {
    return this.billedBy === constants.BILLED_BY_SHOPIFY;
  }

  @computed get billedByDisplay() {
    const option = constants.BILLED_BY_OPTIONS.filter((option) => option.value === this.billedBy)[0];
    if (option) return option.label;
    return "";
  }

  @computed get installedWixApps() {
    return this.data.installed_wix_apps || [];
  }
}


export default AccountStore;


